import * as cartoReactCore from '@carto/react-core'
import { SourceFilters } from '@carto/react-redux'
import { AGGREGATION_TYPES } from '@carto/kepler.gl/constants'

import { SchemaField } from 'features/data-explorer/types'
import { Widget, WidgetType } from '../types'
import { getSchemaColumnFromColumnAlias } from './spatialIndexUtils'
import { ProviderType } from 'features/connections'
import { caseInsensitiveEquals, caseInsensitiveIncludes } from 'features/common/utils/strings'
import { isEqual } from 'lodash'
import { MapDataset } from 'features/maps/types'

export const getFiltersWithoutTimeType = (filters: SourceFilters) => {
  const filtersWithoutTimeType = {}

  Object.entries(filters).forEach(([column, columnData]) => {
    Object.entries(columnData).forEach(([type, typeData]) => {
      if (type !== cartoReactCore._FilterTypes.TIME) {
        filtersWithoutTimeType[column] = { [type]: typeData }
      }
    })
  })
  return filtersWithoutTimeType
}

export function getValidatedFilters(
  filters: Record<string, SourceFilters> | undefined,
  { widgets, mapDatasets }: { widgets: Widget[]; mapDatasets: MapDataset[] }
) {
  if (!filters) return filters

  const validFilters: Record<string, SourceFilters> = {}

  for (const [datasetId, sourceFilters] of Object.entries(filters)) {
    const dataset = mapDatasets.find((d) => d.id === datasetId)
    const providerId = dataset?.providerId
    const newFilters = getValidatedSourceFilters(sourceFilters, { widgets, providerId })

    if (newFilters && Object.keys(newFilters).length > 0) {
      validFilters[datasetId] = newFilters
    }
  }
  return validFilters
}

export function getValidatedSourceFilters(
  filters: SourceFilters | undefined,
  { widgets, schema, providerId }: { widgets?: Widget[]; schema?: SchemaField[] | null; providerId?: ProviderType }
) {
  if (!filters) return filters

  const validFilters: SourceFilters = {}

  for (const [column, columnFilters] of Object.entries(filters)) {
    const resultColumnFilters = {}
    for (const [type, typeData] of Object.entries(columnFilters)) {
      let widget: Widget | undefined
      if (widgets) {
        widget = widgets.find((w) => typeData.owner === w.id)
        if (!widget || !widget.isValid) continue
      }

      if (schema && providerId && widget) {
        // TODO: In remote widgets for spatial indices, we may strip aggregatin from column name here
        // and pass raw column to backend
        const allowedColumNames = getValidColumnsForWidget(widget, providerId, schema)
        if (!caseInsensitiveIncludes(allowedColumNames, column)) continue
      } else if (schema) {
        const schemaColumnNames = schema ? schema.map((field) => field.name) : []

        if (!caseInsensitiveIncludes(schemaColumnNames, column)) continue
      }

      resultColumnFilters[type] = typeData
    }

    if (Object.keys(resultColumnFilters).length > 0) {
      validFilters[column] = resultColumnFilters
    }
  }
  return validFilters
}

export function validateSourceFilters(
  filters: SourceFilters | undefined,
  { widgets, schema, providerId }: { widgets?: Widget[]; schema?: SchemaField[] | null; providerId?: ProviderType }
): [boolean, SourceFilters | undefined] {
  const validFilters = getValidatedSourceFilters(filters, { widgets, schema, providerId })
  return [isEqual(filters, validFilters), validFilters]
}

function getValidColumnsForWidget(widget: Widget, providerId: ProviderType, schema: SchemaField[]) {
  const result: string[] = []

  if (widget.column && schema.find((field) => caseInsensitiveEquals(field.name, widget.column))) {
    result.push(widget.column)
  }
  if (
    (widget.type === WidgetType.CATEGORY || widget.type === WidgetType.PIE) &&
    widget.operationColumn &&
    schema.find((field) => caseInsensitiveEquals(field.name, widget.operationColumn))
  ) {
    result.push(widget.operationColumn)
  }
  if (widget.type === WidgetType.TABLE && widget.columns) {
    result.push(...widget.columns.map(({ field }) => field))
  }

  return result
}

export function migrateSpatialIndexFilters(
  filters: Record<string, SourceFilters> | undefined,
  { widgets }: { widgets: Widget[] }
) {
  if (!filters) return filters

  const migratedFilters: Record<string, SourceFilters> = {}

  for (const [datasetId, sourceFilters] of Object.entries(filters)) {
    const resultDatasetFiltes = {}
    for (const [column, columnFilters] of Object.entries(sourceFilters)) {
      let targetColumn = column
      const resultColumnFilters = {}
      for (const [type, typeData] of Object.entries(columnFilters)) {
        const widget = widgets.find((w) => typeData.owner === w.id)
        if (!widget) continue // ignore filters without widget, just remove them

        // TODO: this could be more robust and check also against actual columns in schema but at the time of loading map we don't have schema of dataset :/
        const isAggregatedColumn = isColumnAggregationExp(column.toLowerCase())
        if (isAggregatedColumn) {
          // when migrating filters over numeric columns with aggregations, ranges/buckets don't match, so drop them
          if (widget.type === WidgetType.HISTOGRAM || widget.type === WidgetType.RANGE) {
            continue
          }

          // remove `_agg` suffix from column name
          targetColumn = getSchemaColumnFromColumnAlias(column)
        }
        resultColumnFilters[type] = typeData
      }
      if (Object.keys(resultColumnFilters).length > 0) {
        resultDatasetFiltes[targetColumn] = resultColumnFilters
      }
    }
    if (Object.keys(resultDatasetFiltes).length > 0) {
      migratedFilters[datasetId] = resultDatasetFiltes
    }
  }
  return migratedFilters
}

export function isColumnAggregationExp(column: string): boolean {
  for (const type of Object.values(AGGREGATION_TYPES) as string[]) {
    if (column.endsWith(`_${type}`)) {
      return true
    }
  }
  return false
}
